import {
  dailyClip,
  dailyPhoto,
  dailyPuzzle,
  loadBoard,
  loadHistory,
  openedOn,
  outcome,
  playableDay,
  recordDay,
  recordedPuzzle,
  ruledOut,
  rowReady,
  saveBoard,
  shareText,
  slots,
  solved,
  today,
  TURNS,
  type BirdleRow,
  type Slot,
} from '../../core/birdle';
import { imageUrl, photosReady } from '../../core/media/image';
import { getSpecies, speciesList } from '../../core/region';
import {
  traitsOf,
  traitValueLabel,
  TRAIT_LABEL,
  TRAIT_PROMPT,
  TRAIT_VALUES,
} from '../../core/traits';
import { el, ensureStyle } from '../dom';
import {
  ensureQuizStyle,
  backBtn,
  missedList,
  type HearHandle,
} from '../components/quiz-chrome';
import { speciesPhoto } from '../components/species-photo';
import { openPickMenu, type PickOption } from '../components/pick-menu';
import { icon } from '../components/icons';
import { traitIcon } from '../components/trait-icons';
import { traitPills } from '../components/trait-pills';
import { waveformPlayer } from '../components/waveform-player';
import { getStripView } from '../prefs';
import type { View } from '../router';
import { t } from '../../strings';
import { readableDay, shortDay } from '../../strings/format';
import type { PhotoAsset, TraitKey } from '../../types';

// The Birdle screen. The rules are all in `src/core/birdle.ts`; this is the board
// they are played on, and the two clues.
//
// Not under /quiz/, and not shaped like one. There is no setup screen, because a
// daily has no length to choose and no voices to pick between; there is no round,
// so no progress bar counting questions; and nothing here grades a card, so there
// is no Undo — a turn is spent, the way a Wordle guess is.
//
// What it does share is the chrome: `ensureQuizStyle()` for the header row, the
// hero frame, the pinned foot and the `.done` panel, and `hearIt` for the speaker,
// whose CSS lives under `.quiz` and which is the reason this root carries that
// class alongside its own.

ensureQuizStyle();

ensureStyle(
  'birdle-css',
  `
  /* The two clues, stacked: the photograph, and under it the app's own sound
     strip — the same control the species page and every round use, drawn as a
     waveform or a spectrogram according to the You screen's Sound strip setting.
     A bare speaker disc came first and said too little: this screen is asking
     what the bird IS, the recording is half the evidence, and a strip you can
     see the shape of is how the rest of the app presents a recording. */
  .quiz.birdle { gap:13px; }
  .quiz.birdle .clues { display:flex; flex-direction:column; gap:8px;
    align-items:center; }
  .quiz.birdle .hero { height:min(23vh, 200px); width:auto; max-width:100%;
    max-height:none; margin:0; background:var(--sunk); overflow:hidden; }
  .quiz.birdle .hero canvas { display:block; width:100%; height:100%; }
  .quiz.birdle .clues .waveform { width:100%; }
  /* The spectrogram rendering is half again as tall as the waveform, and the
     grid below is already the tallest thing on the screen — so it buys its own
     height back out of the photograph, exactly as Sound Match does. */
  .quiz.birdle.tall .hero { height:min(17vh, 148px); }

  /* ---- The grid ----------------------------------------------------------
     A row per turn, a column per lock, on the terms Wordle set: the cell you
     played turns GREEN when it opened its lock and GREY when it did not, and a
     column that has opened stays open all the way down. Grey rather than
     Wordle's yellow-and-grey, because there is no partial credit to report —
     a value the bird holds opens the lock outright and one it doesn't is simply
     not it.

     The bird's column is wider than the four trait columns because a species
     name is three words where a trait value is one. */
  .quiz.birdle .grid { display:grid; grid-template-columns:repeat(4, 1fr) 1.45fr;
    gap:5px; }
  .quiz.birdle .grid .colhead { font-size:8.5px; letter-spacing:.09em;
    text-align:center; color:var(--ink-soft); padding-bottom:1px; }
  /* The glyph over the word, both centred: the icon is what the column is
     scanned by and the word is what settles it, so neither can be the one that
     gets dropped when a value is long. */
  .quiz.birdle .cell { position:relative; min-height:48px; display:flex;
    flex-direction:column; align-items:center; justify-content:center; gap:1px;
    padding:4px 3px; text-align:center; line-height:1.1;
    font-family:var(--font-display); font-size:10px; hyphens:auto;
    border:1.5px solid var(--hairline); border-radius:var(--radius-hand);
    color:var(--ink-soft); }
  .quiz.birdle .cell .tico { opacity:.9; }
  .quiz.birdle .cell .txt { display:block; max-width:100%; }
  /* Waiting to be played, and the row you are on. */
  .quiz.birdle .cell.now { border-color:var(--hairline-strong); background:var(--card);
    color:var(--ink); }
  .quiz.birdle .cell.now.set { border-color:var(--accent); }
  /* Opened this turn, and opened earlier. Both green; the earlier one recedes,
     so the staircase is legible at a glance and the share grid's three states
     and the board's three states are the same three states. */
  .quiz.birdle .cell.hit { background:var(--moss-deep); border-color:transparent;
    color:var(--on-dark); }
  .quiz.birdle .cell.was { background:var(--moss-deep); border-color:transparent;
    opacity:.3; }
  .quiz.birdle .cell.miss { background:var(--sunk); border-color:transparent;
    color:var(--ink-soft); text-decoration:line-through;
    text-decoration-thickness:1px; }
  /* The whole cell is the trigger — see components/pick-menu.ts for why the
     board does not use a native <select>. */
  .quiz.birdle button.cell { font: inherit; font-size:10px; cursor:pointer; }
  .quiz.birdle .cell .caret { position:absolute; right:3px; bottom:3px; width:0; height:0;
    border-left:3px solid transparent; border-right:3px solid transparent;
    border-top:4px solid var(--ink-soft); opacity:.5; }
  /* What the opened locks turned out to be — the bird's WHOLE set for each,
     not just the value that opened it. That a robin eats fruit as well as worms
     is the half a learner who guessed "worms" has not been told yet, and the
     grid cell has room for one value only. */
  .quiz.birdle .learnt { display:flex; flex-direction:column; gap:2px;
    margin-top:-4px; }
  .quiz.birdle .learnt div { font-size:12.5px; }
  .quiz.birdle .learnt .who { color:var(--ink-soft); margin-right:6px; }

  .quiz.birdle .done .tpills { justify-content:center; margin:2px 0 6px; }
  /* The name and, beside it, the way to its page — the Library's own book,
     since the Library is where that page lives. */
  /* Inline, so a name that wraps carries the book on its last line. */
  .quiz.birdle .done h1 .pagelink { display:inline-block; vertical-align:middle;
    margin-left:6px; padding:4px; line-height:0; color:var(--ink-soft); }
  .quiz.birdle .done h1 .pagelink:hover { color:var(--ink); }
  /* The board as played, under the reveal: every guess in its cell. Full width
     like the board it was, and left of the panel's centred text for the same
     reason the strip is. */
  .quiz.birdle .done .guesses { width:100%; margin:10px 0 8px; }
  .quiz.birdle .done .guesses > .label { display:block; margin-bottom:6px; }
  /* The ways out of the board that are not Back, at the end of the header row:
     the calendar of past days, then the traits guide. One gap between them
     rather than two, since each already carries its own padding. */
  .quiz.birdle .bar .guide { margin-right:-6px; color:var(--ink-soft); }
  .quiz.birdle .bar .cal { margin-right:-10px; color:var(--ink-soft); }
  /* Which kind of finish this was, under the headline on a day opened from the
     calendar — the same fact the calendar draws as the weight of a border. */
  .quiz.birdle .done .when { margin:2px 0 10px; }
  /* Share, and the calendar beside it. */
  .quiz.birdle .done .cluerow { display:flex; flex-wrap:wrap; gap:8px;
    justify-content:center; }
  /* The day's strip, carried over from the board: under the photograph, as it
     sat under the pixelated one. Left-aligned inside, since the panel centres
     its text and the strip's corner tags are positioned rather than flowed. */
  .quiz.birdle .done .waveform { margin-top:8px; text-align:left; }
  /* Past days: a date, the bird, the score, one line each. A table in all but
     markup, so the three columns line up down the list. */
  .quiz.birdle .done .past { display:grid; grid-template-columns:auto 1fr auto;
    gap:4px 12px; width:100%; max-width:320px; margin:4px auto 0; text-align:left;
    font-size:13.5px; align-items:baseline; }
  .quiz.birdle .done .past .when, .quiz.birdle .done .past .score {
    font-family:var(--font-mono); font-size:11.5px; color:var(--ink-soft); }
  .quiz.birdle .done .past a { color:var(--ink); }
`,
);

/**
 * How wide the photograph is sampled at, turn by turn.
 *
 * Six steps for six turns, geometric from a smear of colour to a shape you can
 * argue about — the same two ends the eight-turn ladder had, so a shorter day
 * reaches the same last look at the bird, just in bigger strides. The last step is deliberately still pixelated: the
 * budget is spent by the time it is reached, so a legible photograph there would
 * only be a consolation, and a learner who could name the bird from it would be
 * naming it after the day was lost.
 *
 * The first step started at 8 and is now 10 — a third more blocks across, which
 * at this size is the difference between a colour field and a silhouette. Eight
 * was honest to "pixelated nearly to colour" and turned out to be nearly no clue
 * at all: turn one was a sky and a smudge, so the photograph earned its place on
 * the screen only from about turn three.
 */
const LADDER = [10, 14, 21, 30, 44, 64];

/**
 * The pixelated clue.
 *
 * A canvas rather than a filtered `<img>`: a CSS blur is a blur, and the thing
 * this wants is the opposite — hard square blocks, which is what
 * `imageSmoothingEnabled = false` over a tiny offscreen copy gives. The photo
 * arrives as a blob object URL from the pack (see `core/media/image.ts`), which is
 * same-origin, so nothing here taints the canvas.
 *
 * Letterboxed by hand, because the app's rule for a bird photograph is contain and
 * never cover (`species-photo.ts`) and a canvas has no `object-fit`. Cropping to
 * fill would cut whatever is nearest an edge, which on a bird photographed
 * head-high is the top of its head — and this screen is asking what the bird is.
 */
function pixelPhoto(picture: PhotoAsset | undefined, mirrored = false): {
  element: HTMLElement;
  show: (step: number) => void;
  destroy: () => void;
} {
  const canvas = el('canvas', { 'aria-label': t('birdle.clues.photo') }) as HTMLCanvasElement;
  const frame = el('div.hero', {}, canvas);
  let img: HTMLImageElement | null = null;
  let step = 0;
  let small: HTMLCanvasElement | null = null;
  let smallFor = -1;

  const paint = (): void => {
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    if (!w || !h) return;
    const dpr = globalThis.devicePixelRatio || 1;
    const pw = Math.max(1, Math.round(w * dpr));
    const ph = Math.max(1, Math.round(h * dpr));
    if (canvas.width !== pw || canvas.height !== ph) {
      canvas.width = pw;
      canvas.height = ph;
    }
    const g = canvas.getContext('2d');
    if (!g) return;
    g.setTransform(dpr, 0, 0, dpr, 0, 0);
    g.clearRect(0, 0, w, h);
    if (!img?.naturalWidth) return;

    const n = LADDER[Math.min(step, LADDER.length - 1)];
    const ratio = img.naturalHeight / img.naturalWidth;
    if (!small || smallFor !== n) {
      small = document.createElement('canvas');
      small.width = n;
      small.height = Math.max(1, Math.round(n * ratio));
      small.getContext('2d')?.drawImage(img, 0, 0, small.width, small.height);
      smallFor = n;
    }
    // Contain: the whole photograph inside the frame, the frame's own background
    // showing as mount board either side.
    const scale = Math.min(w / img.naturalWidth, h / img.naturalHeight);
    const dw = img.naturalWidth * scale;
    const dh = img.naturalHeight * scale;
    g.imageSmoothingEnabled = false;
    // The day's flip (see `mirrored` on BirdlePuzzle), about the frame's centre
    // line so the letterbox stays where it was.
    if (mirrored) {
      g.translate(w, 0);
      g.scale(-1, 1);
    }
    g.drawImage(small, (w - dw) / 2, (h - dh) / 2, dw, dh);
  };

  void imageUrl(picture?.file ?? '').then((url) => {
    if (!url || !canvas.isConnected) return;
    const loaded = new Image();
    loaded.onload = () => {
      img = loaded;
      small = null;
      paint();
    };
    loaded.src = url;
  });

  // The canvas takes its size from CSS, which is unknown until it is in the
  // document and changes on rotation. Disconnected on teardown, which the router
  // calls — a view that leaked one per visit would repaint boards long gone.
  const ro =
    typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => paint());
  ro?.observe(canvas);

  return {
    element: frame,
    show: (next: number) => {
      step = next;
      small = null;
      paint();
      // Twice, for the first paint: the frame is built before it is appended, so
      // the first call measures 0×0 and draws nothing.
      requestAnimationFrame(paint);
    },
    destroy: () => ro?.disconnect(),
  };
}

/**
 * The half-filled row, kept across a visit to the traits guide.
 *
 * The (i) and the dropdowns' guide links navigate, and navigating tears this
 * view down — so without this, checking what "pendant" means would throw away
 * the four picks made before asking. In memory only: `saveToday` is a
 * whole-settings write kept to once a turn, and a draft is not a turn. A reload
 * still loses it, which is how it always was.
 */
let held: { key: string; row: BirdleRow } | null = null;

/** How many past days the done panel lists. The record keeps more. */
const PAST_SHOWN = 7;

export const birdleView: View = async (root, ctx) => {
  const { day: thisDay, pool } = today();
  // Today's board, or the day the calendar asked for. Anything that is not a
  // day there can be a board for — a typo, tomorrow — is today.
  const day = playableDay(ctx.params.day) ?? thisDay;
  const past = day !== thisDay;

  // Spectrogram strips are taller than waveforms, and this board has no height
  // to spare — see `.birdle.tall` in the CSS.
  const wrap = el(`div.quiz.birdle${getStripView() === 'spectrogram' ? '.tall' : ''}`, {});
  root.append(wrap);

  const [rowsSaved, ready, history] = await Promise.all([
    loadBoard(day, pool),
    photosReady(),
    loadHistory(),
  ]);

  // A past day on record is finished, and stays the bird it was. Its puzzle is
  // drawn again from the day and the packs, which gives the same board for as
  // long as the packs are the ones it was played on; after a pack update the
  // same date would draw a different bird, and then the record is all there is
  // to show (see `recordedPuzzle`). Today is left as it always was: a board for
  // whichever packs are selected now, replayed if that changes mid-day.
  const record = past ? history.find((d) => d.day === day) : undefined;
  const drawn = dailyPuzzle(day, pool, speciesList());
  const asPlayed = !record || drawn?.code === record.code;
  const puzzle = asPlayed ? drawn : recordedPuzzle(record);
  const bird = puzzle ? getSpecies(puzzle.code) : undefined;

  /**
   * The turns played on this day — empty when no saved board is this one.
   *
   * Empty, too, for a day on record whose rows are not: the record keeps the
   * score and the bird, and only the last few boards keep their guesses.
   */
  let rows: BirdleRow[] =
    record && !(asPlayed && puzzle && bird && outcome(puzzle, bird, rowsSaved).over)
      ? []
      : rowsSaved;
  /** Which turn of which day's board a draft belongs to. */
  const draftKey = (): string => `${day}|${pool}|${rows.length}`;
  /** The row being filled in right now — picked back up if it was left for the guide. */
  let draft: BirdleRow = held?.key === draftKey() ? held.row : {};

  /** Where the calendar opens: on the month of the board it was reached from. */
  const calendarPath = `/birdle/calendar?month=${day.slice(0, 7)}`;

  let photo: ReturnType<typeof pixelPhoto> | null = null;
  let strip: ReturnType<typeof waveformPlayer> | null = null;
  /** The speakers on the done panel's wrong guesses. */
  let speakers: HearHandle[] = [];

  const teardown = (): void => {
    photo?.destroy();
    photo = null;
    strip?.stop();
    strip = null;
    for (const speaker of speakers) speaker.stop();
  };

  if (!ready) return renderNoPack(), teardown;
  if (!puzzle || !bird) return (record ? renderGone() : renderEmpty()), teardown;

  // One strip for the whole day, moved from board to board rather than rebuilt
  // on every render. The clip is the day's, not the turn's (see `clip` on
  // BirdlePuzzle), so the same strip is right for every turn AND for the done
  // panel — and a recording still playing when the last row is submitted keeps
  // playing, with its playhead, on the screen that names the bird. The strip
  // says nothing about which bird it is: its own label would name the answer out
  // loud to anyone using a screen reader.
  strip = waveformPlayer(dailyClip(bird, puzzle));
  strip.element.setAttribute('aria-label', t('birdle.clues.recording'));

  render();
  return teardown;

  // ---- screens -------------------------------------------------------------

  function head(text: string): HTMLElement {
    return el(
      'div.qhead',
      {},
      el(
        'div.bar',
        {},
        // A past day was reached from the calendar and goes back to it. Named
        // rather than `ctx.back`, like every Back on a board: a visit to the
        // traits guide leaves history pointing at the guide.
        past
          ? el(
              'button.back.btn.btn-plain',
              {
                onclick: () => ctx.navigate(calendarPath),
                'aria-label': t('birdle.head.toCalendar'),
              },
              icon('arrow-left', { size: 22 }),
            )
          : backBtn(ctx),
        // A past day says which day it is, in the words its share text uses.
        el(
          'div.prompt.label',
          {},
          past ? t('birdle.share.title', { date: shortDay(day) }) : t('birdle.head.title'),
        ),
        el('div.tags', {}),
        el('div.count', {}, text),
        el(
          'button.cal.btn.btn-plain',
          {
            type: 'button',
            title: t('birdle.head.calendar'),
            'aria-label': t('birdle.head.calendar'),
            onclick: () => ctx.navigate(calendarPath),
          },
          icon('calendar', { size: 20 }),
        ),
        el(
          'button.guide.btn.btn-plain',
          {
            type: 'button',
            title: t('birdle.head.guide'),
            'aria-label': t('birdle.head.guide'),
            onclick: () => ctx.navigate('/traits'),
          },
          icon('info', { size: 20 }),
        ),
      ),
    );
  }

  /** A link to one axis of the traits guide, for the foot of its dropdown. */
  function guideLink(slot: TraitKey): HTMLElement {
    const link = el('a', { href: `#/traits?axis=${slot}` }, t('birdle.board.guideLink'));
    link.addEventListener('click', (e) => {
      e.preventDefault();
      ctx.navigate(`/traits?axis=${slot}`);
    });
    return link;
  }

  function renderNoPack(): void {
    wrap.replaceChildren(
      head(''),
      el('div.setup', {},
        el('h1', {}, t('birdle.noPack.title')),
        el('p.blurb', {}, t('birdle.noPack.body')),
        el('button.btn.btn-primary', { onclick: () => ctx.navigate('/you') }, t('birdle.noPack.download')),
      ),
    );
  }

  function renderEmpty(): void {
    wrap.replaceChildren(
      head(''),
      el('div.setup', {},
        el('h1', {}, t('birdle.empty.title')),
        el('p.blurb', {}, t('birdle.empty.body')),
      ),
    );
  }

  /** A day on record whose bird is in a pack that is not selected now. */
  function renderGone(): void {
    wrap.replaceChildren(
      head(''),
      el('div.setup', {},
        el('h1', {}, t('birdle.gone.title')),
        el('p.blurb', {}, t('birdle.gone.body', { date: readableDay(day) })),
      ),
    );
  }

  /**
   * How the day stands: off the record for a past day that has one, since that
   * is the finish that counted, and off the rows for a board still in play.
   */
  function standing(): { turns: number; won: boolean; over: boolean } {
    return record
      ? { turns: record.turns, won: record.won, over: true }
      : outcome(puzzle!, bird!, rows);
  }

  function render(): void {
    const state = standing();
    if (state.over) return renderDone();

    // A fresh canvas per turn; the last one's observer goes with it.
    photo?.destroy();
    photo = pixelPhoto(dailyPhoto(bird!, puzzle!), puzzle!.mirrored);

    const submit = el('button.next.btn.btn-primary', { type: 'button' }, t('birdle.board.submit')) as HTMLButtonElement;
    submit.addEventListener('click', () => {
      if (submit.disabled) return;
      rows = [...rows, draft];
      draft = {};
      held = null;
      void saveBoard(day, pool, rows);
      render();
    });
    const refresh = (): void => {
      submit.disabled = !rowReady(puzzle!, bird!, rows, draft);
    };

    wrap.replaceChildren(
      head(t('birdle.board.turn', { turn: rows.length + 1, max: TURNS })),
      el('div.clues', {}, photo.element, strip!.element),
      // Above the grid, not below it: it is the dossier built so far, it reads
      // under the photograph as part of what is known about the bird, and down
      // there it sat in the fade the pinned bar draws over the scroll.
      ...learnt(),
      grid(refresh),
      // The shared pinned foot, which already owns the bleed to the screen
      // edges, the opaque ground and the gap above the nav bar.
      el('div.after', {}, submit),
    );
    photo.show(rows.length);
    refresh();
  }

  /**
   * The board: a column per lock, a row per turn.
   *
   * Every row of the budget is drawn from the start rather than appearing as it
   * is played — six empty rows say what the budget IS, which is the one thing
   * a turn counter alone cannot show, and it also stops the page reflowing under
   * the thumb on every submit.
   */
  function grid(refresh: () => void, done = false): HTMLElement {
    const board = slots(puzzle!);
    const turn = openedOn(puzzle!, bird!, rows);
    const struck = ruledOut(puzzle!, bird!, rows);
    const box = el('div.grid', {});

    for (const slot of board) {
      box.append(el('div.colhead.label', {}, slot === 'name' ? t('birdle.board.bird') : TRAIT_LABEL[slot]));
    }

    // Finished, only the rows that were played: the empty ones were the budget,
    // and the budget is spent or no longer matters.
    for (let i = 0; i < (done ? rows.length : TURNS); i++) {
      for (const slot of board) {
        const at = turn[slot];
        // Already open when this row came round: green, receded, and EMPTY.
        // The column has nothing left to ask, so there is no pick to report —
        // and repeating the value down five rows would make a solved column the
        // loudest thing on the board. What it turned out to be is said once, in
        // full, under the grid. This is the share grid's ⬜ and means the same.
        if (at && at < i + 1) {
          box.append(el('div.cell.was', {}));
          continue;
        }
        if (i < rows.length) {
          const picked = rows[i][slot];
          box.append(
            el(
              `div.cell.${at === i + 1 ? 'hit' : 'miss'}`,
              {},
              picked && slot !== 'name' ? (traitIcon(slot, picked, { size: 19 }) ?? '') : '',
              el('span.txt', {}, picked ? label(slot, picked) : ''),
            ),
          );
          continue;
        }
        // The row being filled in, and then the ones after it.
        box.append(i === rows.length ? liveCell(slot, struck[slot], refresh) : el('div.cell', {}));
      }
    }
    return box;
  }

  /** A cell on the current row: the value it is showing, and the menu behind it. */
  function liveCell(slot: Slot, struck: Set<string>, refresh: () => void): HTMLElement {
    const cell = el('button.cell.now', {
      type: 'button',
      'aria-label': slot === 'name' ? t('birdle.board.whichBird') : TRAIT_PROMPT[slot],
    }) as HTMLButtonElement;

    const draw = (): void => {
      const value = draft[slot];
      cell.replaceChildren(
        value && slot !== 'name' ? (traitIcon(slot, value, { size: 19 }) ?? '') : '',
        el('span.txt', {}, value ? label(slot, value) : '—'),
        el('span.caret', {}),
      );
      cell.classList.toggle('set', !!value);
    };

    cell.addEventListener('click', () =>
      openPickMenu(cell, {
        title: slot === 'name' ? t('birdle.board.whichBird') : TRAIT_PROMPT[slot],
        options: menuOptions(slot, struck),
        current: draft[slot],
        foot: slot === 'name' ? null : guideLink(slot),
        onPick: (value) => {
          draft[slot] = value;
          held = { key: draftKey(), row: draft };
          draw();
          refresh();
        },
      }),
    );
    draw();
    return cell;
  }

  /**
   * What one lock's menu offers.
   *
   * Traits lead with their glyph. The birds lead with nothing — thirty
   * thumbnails would be thirty photographs of birds on a screen whose whole
   * question is which bird this is, and the answer is one of them. The names
   * alone, alphabetical, are what a list of thirty wants anyway. Spent values
   * stay on the list, struck out.
   */
  function menuOptions(slot: Slot, struck: Set<string>): PickOption[] {
    if (slot === 'name') {
      return puzzle!.names
        .map((code) => ({ code, name: getSpecies(code)?.common ?? code }))
        .sort((a, b) => a.name.localeCompare(b.name))
        .map(({ code, name }) => ({ value: code, label: name, spent: struck.has(code) }));
    }
    return TRAIT_VALUES[slot].map((value) => ({
      value,
      label: traitValueLabel(slot, value),
      lead: traitIcon(slot, value, { size: 19 }),
      spent: struck.has(value),
    }));
  }

  /** What a stored value reads as in a cell. */
  function label(slot: Slot, value: string): string {
    if (slot === 'name') return getSpecies(value)?.common ?? value;
    return traitValueLabel(slot, value);
  }

  /**
   * Under the grid: every lock that has opened, with the bird's WHOLE set for it.
   *
   * The grid can only ever show one value per cell, and for four of the five axes
   * one value is not the fact — a bird that lives in four kinds of country has
   * told you about one of them. This line is where the puzzle actually teaches.
   */
  function learnt(): HTMLElement[] {
    const open = solved(puzzle!, bird!, rows);
    const rowsOut = slots(puzzle!)
      .filter((slot) => open.has(slot))
      .map((slot) =>
        slot === 'name'
          ? el('div', {}, el('span.who.label', {}, t('birdle.board.bird')), bird!.common)
          : el(
              'div',
              {},
              el('span.who.label', {}, TRAIT_LABEL[slot]),
              traitsOf(bird!)[slot].map((v) => traitValueLabel(slot, v)).join(', '),
            ),
      );
    return rowsOut.length ? [el('div.learnt', {}, ...rowsOut)] : [];
  }

  function renderDone(): void {
    const state = standing();
    // The board's own day, not the day it is being looked at on.
    const date = shortDay(day);
    const text = shareText(puzzle!, bird!, rows, { date });
    // A day on record with its rows gone has a score and a bird and no board:
    // nothing to draw a staircase from, here or in the share text.
    const played = rows.length > 0;

    // The OS share sheet where there is one, which is most phones — the result
    // is going to a message thread, and the sheet is how a phone gets it there.
    // The clipboard where there is not, and as the fallback when the sheet
    // itself fails; a sheet the learner dismissed is not a failure.
    const canShare = typeof navigator.share === 'function';
    const copy = el(
      'button.btn.btn-quiet',
      { type: 'button' },
      canShare ? t('birdle.done.share') : t('birdle.done.copy'),
    ) as HTMLButtonElement;
    const toClipboard = (): void => {
      void (navigator.clipboard?.writeText(text) ?? Promise.reject(new Error('no clipboard')))
        .then(() => {
          copy.textContent = t('birdle.done.copied');
        })
        .catch(() => {
          // No clipboard (an insecure origin, or a browser that refuses): say so
          // rather than leaving a button that looks as though it worked.
          copy.textContent = t('birdle.done.copyFailed');
        });
    };
    copy.addEventListener('click', () => {
      if (!canShare) return toClipboard();
      navigator.share({ text }).catch((e: unknown) => {
        if ((e as Error)?.name !== 'AbortError') toClipboard();
      });
    });

    // On record, then the days before it. Idempotent, so a revisit writes
    // nothing — and it is `recordDay` that decides whether this finish was on
    // the day or after it, from the clock at the moment it is first recorded.
    const earlier = el('div.past-days', {});
    void (record
      ? Promise.resolve()
      : recordDay({ day, code: bird!.code, won: state.won, turns: state.turns })
    )
      .then(loadHistory)
      .then((days) => {
        // Today's panel only. A day opened from the calendar came from the
        // fuller version of this list and is one tap from it again.
        const shown = past ? [] : days.filter((d) => d.day !== day).slice(0, PAST_SHOWN);
        const rowsOut = shown.flatMap((d) => {
          const sp = getSpecies(d.code);
          const name = el('a', { href: `#/species/${d.code}` }, sp?.common ?? d.code);
          name.addEventListener('click', (e) => {
            e.preventDefault();
            ctx.navigate(`/species/${d.code}`);
          });
          return [
            el('span.when', {}, shortDay(d.day)),
            name,
            el(
              'span.score',
              {},
              d.won
                ? t('birdle.share.won', { turns: d.turns, max: TURNS })
                : t('birdle.share.lost', { max: TURNS }),
            ),
          ];
        });
        if (!rowsOut.length || !earlier.isConnected) return;
        earlier.replaceChildren(
          el('div.missed', {}, el('span.label', {}, t('birdle.done.pastDays'))),
          el('div.past', {}, ...rowsOut),
        );
      });

    // The birds named instead, in the order they were guessed — each a tap from
    // its page, with a speaker in the day's own voice, because the strip above is
    // the recording they were mistaken for and a call against a song compares
    // nothing. A wrong name is struck off the list, so none can come twice.
    const wrong = rows
      .map((row) => row.name)
      .filter((code): code is string => !!code && code !== bird!.code);
    for (const speaker of speakers) speaker.stop();
    const guesses = missedList(
      wrong.map((code) => ({ code, type: puzzle!.voice })),
      ctx,
      t('birdle.done.guessedInstead'),
    );
    speakers = guesses?.speakers ?? [];

    // The bird's page. A real href, so it can be long-pressed or opened in a
    // tab; the handler only because the router owns navigation.
    const pageLabel = t('birdle.done.speciesPage', { name: bird!.common });
    const pageLink = el(
      'a.pagelink',
      { href: `#/species/${bird!.code}`, title: pageLabel, 'aria-label': pageLabel },
      icon('book', { size: 20 }),
    );
    pageLink.addEventListener('click', (e) => {
      e.preventDefault();
      ctx.navigate(`/species/${bird!.code}`);
    });

    const calendar = el(
      'button.btn.btn-quiet',
      { type: 'button', onclick: () => ctx.navigate(calendarPath) },
      icon('calendar', { size: 17 }),
      t('birdle.done.calendar'),
    );

    // The pixelated board is finished with; its canvas goes, the strip stays.
    photo?.destroy();
    photo = null;

    wrap.replaceChildren(
      head(
        state.won
          ? t('birdle.share.won', { turns: state.turns, max: TURNS })
          : t('birdle.share.lost', { max: TURNS }),
      ),
      el('div.done', {},
        el('div.big', {}, state.won ? t('birdle.done.won', { turns: state.turns }) : t('birdle.done.lost')),
        // Only on a day that is on record, which every past day is by the time
        // it is looked at twice: the first draw of a board just finished is
        // still being written down, and has just been played anyway.
        record
          ? el(
              'div.when.meta',
              {},
              record.late
                ? t('birdle.done.playedLate', { date: readableDay(day) })
                : t('birdle.done.playedOnDay', { date: readableDay(day) }),
            )
          : null,
        // Facing the way the clue did, so the reveal is recognisably the
        // picture the learner has been squinting at.
        speciesPhoto(dailyPhoto(bird!, puzzle!), {
          alt: bird!.common,
          mirror: puzzle!.mirrored,
        }).element,
        strip!.element,
        el('h1', {}, bird!.common, pageLink),
        el('div.sci.meta', {}, bird!.sci),
        traitPills(bird!, ctx),
        played
          ? el('div.guesses', {}, el('span.label', {}, t('birdle.done.yourGuesses')), grid(() => {}, true))
          : null,
        el('div.cluerow', {}, played ? copy : null, calendar),
        guesses?.element ?? null,
        earlier,
        past ? null : el('p.blurb', {}, t('birdle.done.tomorrow')),
      ),
    );
  }

};
